import React, { Component } from 'react';
import { Button, Row, Col, Alert } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';

import Icon from '../../common/Icon';
import Paths from '../../../types/Paths';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import {
  getStorageAndParse,
  stringifyAndSetStorage
} from '../../../util/storage';
import { storageKeys } from '../../../util/constants';
import ButtonLink from '../../common/ButtonLink';
import ProductStore from '../../../store/ProductStore';
import ProductClass from '../../../types/ProductClass';
import { calculateNearestProductQuantityWithQuantityStep } from '../../../util/number';
import RouteService from '../../../services/RouteService';

@observer
class ProductAddToWishList extends Component {
  constructor(props) {
    super(props);

    this.addProductToWishlist = this.addProductToWishlist.bind(this);
    this.onAlertDismiss = this.onAlertDismiss.bind(this);
  }

  state = {
    productAddedBanner: false,
    alertVisible: false
  };

  storage = [];

  componentDidMount() {
    this.storage = getStorageAndParse(storageKeys.wishlist);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps !== this.props) {
      this.setState({ alertVisible: false });
    }
  }

  addProductToWishlist = () => {
    const { product, quantity } = this.props;
    const newQuantity = calculateNearestProductQuantityWithQuantityStep(
      quantity,
      this.getQuantityStep(product)
    );
    this.findProduct(product, Math.ceil(newQuantity));
    stringifyAndSetStorage(storageKeys.wishlist, this.storage);
    this.showProductAddedToWishListBanner();
  };

  findProduct = (product, newQuantity) => {
    const productId = this.getProductIdByProductClass(product);
    const productFound = this.storage.find((p) => p.id === productId);
    if (productFound) {
      this.updateProduct(productFound, newQuantity);
      this.resetProductVariationChoices();
    } else {
      this.addProduct(product, newQuantity);
      this.resetProductVariationChoices();
    }
  };

  updateProduct = (productFound, newQuantity) => {
    const productIndex = this.storage.findIndex(
      (p) => p.id === productFound.id
    );
    if (newQuantity === 1) {
      productFound.quantity++;
    } else {
      productFound.quantity = newQuantity;
    }
    this.storage.splice(productIndex, 1, productFound);
  };

  addProduct = (product, newQuantity) => {
    const productListProduct = this.initializeProductListProduct(
      product,
      newQuantity
    );
    if (this.props.accountStore.account.is_logged) {
      this.ifLoggedInSaveToDatabase(productListProduct, newQuantity);
    } else {
      this.storage.push(productListProduct);
    }
  };

  initializeProductListProduct = (product, quantity) => {
    const { productStore, routeService } = this.props;
    this.setVariationIfMultiProduct(productStore, product);
    return {
      id: this.getProductIdByProductClass(product),
      recurring_order_type: product.recurring_order_type,
      multiproduct_id: product.multiproduct_id,
      product_id: product.id,
      name: product.name,
      quantity: Number(quantity),
      class: product.class,
      collection: product.collection,
      multi: product.multi,
      multiproduct_title: product.multiproduct_title,
      stock_unit: product.stock_unit,
      price_info: product.price_info,
      total_price: product.price_info.price,
      link: routeService.getCanonicalProductPath(product),
      image: this.getSmallProductImageOrDefault(product.images),
      package_size: product.package_size,
      availability_html: product.availability_html,
      available_for_purchase: product.can_be_ordered_out_of_stock,
      variations: this.resetVariationsIfProductIsNormal(productStore, product)
    };
  };

  getProductIdByProductClass = (product) => {
    return product.class === ProductClass.COLLECTION ||
      product.class === ProductClass.MULTI
      ? this.props.activeProductId
      : product.id;
  };

  setVariationIfMultiProduct = (productStore, product) => {
    if (product.class === ProductClass.MULTI_CHILD) {
      productStore.setProductVariationColumn(`${product.model}`);
    }
  };

  getSmallProductImageOrDefault = (images) => {
    return images[0] ? images[0].sizes.small : null;
  };

  resetVariationsIfProductIsNormal = (productStore, product) => {
    if (product.class === ProductClass.SINGLE) {
      this.resetProductVariationChoices();
      return this.getProductVariations(productStore);
    } else {
      return this.getProductVariations(productStore);
    }
  };

  resetProductVariationChoices = () => {
    const { productStore } = this.props;
    productStore.setProductVariationColumn(null);
    productStore.setProductVariationRow(null);
  };

  getProductVariations = (productStore) => {
    return [
      productStore.getProductVariationColumn(),
      productStore.getProductVariationRow()
    ];
  };

  ifLoggedInSaveToDatabase = (productListProduct, newQuantity) => {
    const { productStore } = this.props;
    const newProduct = {
      extendedId: productListProduct.id,
      quantity: newQuantity
    };
    productStore
      .saveProduct(newProduct)
      .then((response) => {
        if (response.status === 200) {
          this.showProductAddedToWishListBanner();
        }
      })
      .catch((err) => console.error(err));
  };

  getQuantityStep = (product) => {
    return product && product.sellInPackage ? product.package_size : 1;
  };

  showProductAddedToWishListBanner = () => {
    this.setState({
      productAddedBanner: true,
      alertVisible: true
    });
  };

  onAlertDismiss = () => {
    this.setState({ alertVisible: false });
  };

  addToWishListButton = () => {
    return (
      <Button
        color="primary"
        className="ProductAddToWishList__button"
        onClick={this.addProductToWishlist.bind(this)}
        disabled={this.props.disabled}
      >
        <Icon name="heart" />
        <FormattedMessage
          id="product.AddWishListTitle"
          defaultMessage="Add to wishlist"
        />
      </Button>
    );
  };

  productAddedBanner = () => {
    if (this.state.productAddedBanner) {
      return (
        <Alert color="secondary" isOpen={this.state.alertVisible}>
          <FormattedMessage
            id="product.ProductAddedToWishlist"
            defaultMessage={`Product added to wishlist. You can view it {link}.`}
            values={{
              link: (
                <ButtonLink onClick={this.navigateToWishList}>
                  <FormattedMessage
                    id="product.ProductAddedToWishlist_here"
                    defaultMessage="here"
                  />
                </ButtonLink>
              )
            }}
          />
        </Alert>
      );
    }
  };

  navigateToWishList = () => {
    this.props.history.push(Paths.WishList);
  };

  render() {
    return (
      <div className="ProductAddToWishList">
        <Row className="pr-3">
          <Col xs={12}>{this.addToWishListButton()}</Col>
          <Col xs={12}>{this.productAddedBanner()}</Col>
        </Row>
      </div>
    );
  }
}

ProductAddToWishList.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  product: PropTypes.object.isRequired,
  activeProductId: PropTypes.string,
  disabled: PropTypes.bool
};

export default inject('accountStore', 'productStore', 'routeService')(
  ProductAddToWishList
);



// WEBPACK FOOTER //
// ./src/components/product/ProductAddToWishList/index.jsx